import "./style.scss";
import { Dispatch, FC, SetStateAction, useState } from "react";
import { IFiltersList } from "@/src/types/applicationOnline";
import { applicationPageSvgConst } from "@/src/svg/applicationsPage/applicationsPageSvgExport";
import BtnDefault from "../../BtnDefault/BtnDefault";
import { useDispatch } from "react-redux";
import { setFilterStatus, updateFilterResult } from "@/src/store/slices/TasksPage/tasksFiltersSlice";

interface ITasksFilterPopUp {
  id: string;
  tableFiltersList: IFiltersList | undefined;
  filterPopUp: string | null;
  setFilterPopUp: Dispatch<SetStateAction<string | null>>;
}

const TasksFilterPopUp: FC<ITasksFilterPopUp> = ({ id, tableFiltersList, filterPopUp, setFilterPopUp }) => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState<string[]>([]);
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>(new Date().toISOString().slice(0, 10));
  const [updatedDateStart, setUpdatedDateStart] = useState<string>("");
  const [updatedDateEnd, setUpdatedDateEnd] = useState<string>(new Date().toISOString().slice(0, 10));
  const [search, setSearch] = useState<string>("");

  const toStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => (item == null ? "" : String(item).trim()))
      .filter((item) => item !== "" && item.toLowerCase() !== "null");
  };

  const handleEventToggle = (value: string) => {
    setSelected((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const handleEventChooseAll = (mass: IFiltersList | undefined) => {
    switch (id) {
      case "color_star":
        setSelected(toStringArray(mass?.color_star));
        break;
      case "assignee":
      case "assigneeId":
        setSelected(toStringArray(mass?.assignee));
        break;
      case "country":
        setSelected(toStringArray(mass?.country));
        break;
      case "course":
        setSelected(toStringArray(mass?.course));
        break;
      case "status":
        setSelected(toStringArray(mass?.status));
        break;
      case "type":
        setSelected(toStringArray(mass?.type));
        break;
      case "is_read":
        setSelected(toStringArray(mass?.is_read));
        break;
      default:
        setSelected([]);
    }
  };

  const handleEventClearFilters = () => {
    setSelected([]);
    setDateStart("");
    setDateEnd("");
    setUpdatedDateStart("");
    setUpdatedDateEnd("");
    setSearch("");
  };

  const handleApply = () => {
    dispatch(setFilterStatus("All"));
    dispatch(updateFilterResult({ key: id, value: selected }));
    setFilterPopUp(null);
  };

  if (id === "color_star") {
    return (
      <div className={`tasks-page-filter ${id === filterPopUp ? "show" : ""}`}>
        <div className="tasks-page-filter-title">
          <p>Star</p>
        </div>
        <ul className="tasks-page-filter-ul">
          {toStringArray(tableFiltersList?.color_star).map((item, index) => (
            <li className="tasks-page-filter-li" key={index}>
              <label className="tasks-page-filter-li-label">
                <input type="checkbox" checked={selected.includes(item)} onChange={() => handleEventToggle(item)} />
                <span className="checkmark"></span>
              </label>
              {item === "default" && <applicationPageSvgConst.StarDefaultSvg />}
              {item === "green" && <applicationPageSvgConst.StarGreenSvg />}
              {item === "yellow" && <applicationPageSvgConst.StarYellowSvg />}
              {item === "black" && <applicationPageSvgConst.StarBlackSvg />}
            </li>
          ))}
        </ul>
        <div className="tasks-page-filter-actions">
          <button type="button" onClick={() => handleEventChooseAll(tableFiltersList)}>
            Choose all
          </button>
          <button type="button" onClick={handleEventClearFilters}>
            Clear filters
          </button>
        </div>
        <div className="tasks-page-filter-buttons">
          <BtnDefault onClick={handleApply}>
            <p>Ok</p>
          </BtnDefault>
          <BtnDefault onClick={() => setFilterPopUp(null)}>
            <p>Cancel</p>
          </BtnDefault>
        </div>
      </div>
    );
  }

  if (id === "date" || id === "updated") {
    const isUpdated = id === "updated";
    const startDate = isUpdated ? updatedDateStart : dateStart;
    const endDate = isUpdated ? updatedDateEnd : dateEnd;
    const setStart = isUpdated ? setUpdatedDateStart : setDateStart;
    const setEnd = isUpdated ? setUpdatedDateEnd : setDateEnd;
    const startDateKey = isUpdated ? "start_updated_date" : "start_date";
    const endDateKey = isUpdated ? "end_updated_date" : "end_date";

    const handleApplyDate = () => {
      dispatch(updateFilterResult({ key: startDateKey, value: startDate || null }));
      dispatch(updateFilterResult({ key: endDateKey, value: endDate || null }));
      setFilterPopUp(null);
    };

    return (
      <div className={`tasks-page-filter ${id === filterPopUp ? "show" : ""}`}>
        <div className="tasks-page-filter-title">
          <p>{isUpdated ? "Updated" : "Date"}</p>
        </div>
        <div className="tasks-page-filter-date">
          <input type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStart(e.target.value)} />
          <span className="tasks-page-filter-date-to">to</span>
          <input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <div className="tasks-page-filter-actions">
          <button type="button" onClick={handleEventClearFilters}>
            Clear filters
          </button>
        </div>
        <div className="tasks-page-filter-buttons">
          <BtnDefault onClick={handleApplyDate}>
            <p>Ok</p>
          </BtnDefault>
          <BtnDefault onClick={() => setFilterPopUp(null)}>
            <p>Cancel</p>
          </BtnDefault>
        </div>
      </div>
    );
  }

  if (id === "is_read") return null;

  const values = toStringArray(tableFiltersList?.[id as keyof IFiltersList]);

  return (
    <div className={`tasks-page-filter ${id === filterPopUp ? "show" : ""}`}>
      <div className="tasks-page-filter-title">
        <p>{id}</p>
      </div>
      <div className="tasks-page-filter-search">
        <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <ul className="tasks-page-filter-ul">
        {values
          .filter((item) => item.toLowerCase().includes(search.toLowerCase()))
          .map((item, index) => (
            <li className="tasks-page-filter-li" key={index}>
              <label className="tasks-page-filter-li-label">
                <input type="checkbox" checked={selected.includes(item)} onChange={() => handleEventToggle(item)} />
                <span className="checkmark"></span>
              </label>
              <p>{item}</p>
            </li>
          ))}
      </ul>
      <div className="tasks-page-filter-actions">
        <button type="button" onClick={() => handleEventChooseAll(tableFiltersList)}>
          Choose all
        </button>
        <button type="button" onClick={handleEventClearFilters}>
          Clear filters
        </button>
      </div>
      <div className="tasks-page-filter-buttons">
        <BtnDefault onClick={handleApply}>
          <p>Ok</p>
        </BtnDefault>
        <BtnDefault onClick={() => setFilterPopUp(null)}>
          <p>Cancel</p>
        </BtnDefault>
      </div>
    </div>
  );
};

export default TasksFilterPopUp;
